// Namespaces per docs/rules/i18n.md; only `common.*` is deduped.
export default {
  common: {
    add: 'Add',
    apply: 'Apply',
    back: 'Back',
    brands: 'Brands',
    cancel: 'Cancel',
    close: 'Close',
    continue: 'Continue',
    delete: 'Delete',
    discard: 'Discard',
    edit: 'Edit',
    goBack: 'Go Back',
    learnMore: 'Learn More',
    logIn: 'Log In',
    logOut: 'Log Out',
    name: 'Name',
    next: 'Next',
    nextItem: 'Next item',
    optional: '(optional)',
    other: 'Other',
    previous: 'Previous',
    previousItem: 'Previous item',
    resetAll: 'Reset all',
    retry: 'Retry',
    save: 'Save',
    search: 'Search',
    seeMore: 'See More',
    selectAll: 'Select All',
    tags: 'Tags',
    tip: 'Tip',
    tryAgain: 'Try again',
    unscheduled: 'Unscheduled',
  },
  ui: {
    noConnectionPlaceholder: {
      title: '{channel} Analytics',
      sectionTitle: 'Connect your {channel} account',
      connect: 'Connect {channel}',
    },
    scopedBrandBanner: {
      title: 'You’re working in {brandName}',
      message:
        'Changes made here apply to this brand. Your selected brand is still {selectedBrandName}.',
    },
  },
  app: {
    community: {
      savedReplyFilterDropdown: {
        label: 'Send Saved Reply:',
        missing: 'Selected reply no longer exists',
        noValue: 'No value selected',
        title: 'Saved Replies',
      },
      agentTasks: {
        tagGuidanceTitle: 'Classify messages first, then take action.',
        tagGuidanceMessage:
          'Use auto-tagging to apply Tags across your inbox without limiting by channel, sentiment, keywords, or other filters. Then use Tags as filters in Agent Tasks to take action on the messages that matter.',
        tagsSettings: 'Go to Tags in Settings',
        tagConflict:
          'Description-based auto-tagging is enabled for these tags: {tags}. Remove these tag actions or turn off auto-tagging in Settings.',
        textAnalysisRestriction:
          'Sentiment and spam conditions require text analysis access. Remove these conditions to continue.',
        textAnalysisTooltip: 'Sentiment and spam conditions require text analysis access.',
      },
    },
    adContentDrawer: {
      title: 'Ad content',
      loadError: 'Unable to load ad content.',
      loadMoreError: 'Unable to load more ad content.',
    },
    dashboards: {
      metrics: {
        groupedRankingDisabled: 'This metric is not available with the selected Content Level.',
        placementUnavailable:
          'Non-additive metrics are unavailable while a Placement filter is active.',
        unavailable: 'This metric is unavailable for the current report.',
      },
      sorting: {
        invalidSavedSort:
          'This saved sort is invalid. {reason} Choose an available metric and sort order.',
      },
    },
    visionChat: { working: 'Working on your request' },
    adInsightsDrawer: {
      adTitle: 'Ad Insights',
      adNameTitle: 'Ad Name Insights',
      creativeTitle: 'Creative Insights',
      openDetails: 'Open insights for {name}',
      unavailableTitle: 'Ad insights unavailable',
      unavailableMessage: 'This content could not be found or you do not have access to it.',
      invalidLink: 'This ad insights link is invalid or no longer supported.',
      disabledMessage: 'Ad insights are not available for this account.',
      errorTitle: 'Something went wrong.',
      errorMessage: 'Unable to load ad insights. Please try again.',
      previewLabel: 'Ad preview',
      actionsLabel: 'Ad actions',
      sectionsLabel: 'Ad insights',
      loadingLabel: 'Loading ad insights',
      loadingMessage: 'Loading ad insights…',
    },
    analytics: {
      channelLayout: {
        navigationTitle: 'Analytics',
      },
      channelNoAccessBanner: {
        noBrandPermission: "Looks like this feature isn't included in your plan!",
        noUserPermission: "Looks like you don't have permission to use this feature!",
        noAccessMessage:
          "Dash Social brings you all of the insights you love, including {channel}! Ask your Dash Social administrator to provide you access. Or if this feature isn't in your package, contact your Customer Success Representative to try it out!",
      },
    },
    boards: {
      index: {
        channelTitle: '{channel} Boards',
        instagramStoriesTitle: 'Instagram Story Boards',
      },
    },
    brandGovernance: {
      navLabel: 'Governance settings',
      drawer: {
        selectBrands: 'Select brands',
        searchBrands: 'Search',
        brandSafety: {
          label: 'Describe your rules',
          placeholder: "Describe your brand's rules here",
        },
        otherRules: {
          label: 'Additional information',
          placeholder: 'Add additional information to guide Vision here',
        },
      },
      fontSelect: {
        label: 'Font',
        placeholder: 'Select a font',
        searchPlaceholder: 'Search fonts',
      },
      header: {
        eyebrow: 'Vision knows your content, sentiment, performance, and more',
        title: 'Now give it {accent}',
        titleAccent: 'your brand rules',
        subtitle: 'Each rule you add shows Vision what on-brand looks like for you.',
      },
      status: {
        notSetUp: 'Not set up',
        usedByVision: 'Used by Vision',
        optional: 'Optional',
      },
      cards: {
        brandGuidelines: {
          title: 'Brand guidelines',
          description:
            'Upload your brand fonts and logos to check your content and create new content that matches.',
          addCta: 'Add brand guidelines',
          editCta: 'Edit brand guidelines',
        },
        brandSafety: {
          title: 'Brand safety',
          description:
            "List anything your brand avoids, like competitors, alcohol, or certain keywords, and we'll flag content that includes it.",
          addCta: 'Add exclusions',
          editCta: 'Edit exclusions',
        },
        otherRules: {
          title: 'Other rules',
          description:
            "Anything else we should know? Add rules or preferences that aren't already covered, and we'll follow them across the platform.",
          addCta: 'Add rules',
          editCta: 'Edit rules',
        },
      },
    },
    campaigns: {
      adCampaignsPopover: {
        chip: '{count} {source} Ad Campaigns | {count} {source} Ad Campaign | {count} {source} Ad Campaigns',
        chipLoading: '{source} Ad Campaigns',
        manage: 'Manage {source} Ad Campaigns',
        manuallyAdded: 'Manually Added',
        autoAdded: 'Auto Added',
        autoAddedTooltip:
          'These campaigns match your auto-add keyword. Any new campaign with a matching name is added automatically.',
        loadError: 'Ad campaigns could not be loaded at this time.',
      },
      adContentReport: {
        title: 'Ads',
        tooltip:
          "Shows performance for the Meta ad campaigns you've selected, based on your reporting period and filters",
      },
      reviewRecipients: {
        addMissingEmailsTitle: 'Add Missing Emails',
        recipientsTitle: 'Recipients',
      },
      emailComposer: {
        title: 'Compose Email',
        send: 'Send to {count} Creator | Send to {count} Creators',
        loadingRecipients: 'Loading recipients',
        recipients: 'Recipients ({count})',
        creator: 'Creator',
        emailAddress: 'Email Address',
        recipientLoadErrorTitle: "Recipients couldn't be loaded",
        recipientLoadErrorMessage: 'Try loading the selected creators again.',
        tryAgain: 'Try Again',
        missingInformation:
          'One creator is missing information used in this email | {count} creators are missing information used in this email',
        sendError: "We couldn't send this email. Please try again.",
        partialSendError:
          'Some emails were already queued. Try again to finish sending to the remaining creators.',
        sending: 'Email is being sent. | Emails are being sent.',
        cc: 'CC',
        bcc: 'BCC',
        addEmailAddresses: 'Add email addresses',
        subject: 'Subject',
        enterSubject: 'Enter subject',
        subjectRequired: 'Subject is required',
        message: 'Message',
        enterMessage: 'Enter message',
        messageRequired: 'Message is required',
        insertMessageVariable: 'Insert message variable',
        insertPersonalizedContent: 'Insert personalized content',
        insertDynamicLink: 'Insert dynamic link',
        dynamicLinks: 'Dynamic Links',
        dynamicPlaceholders: 'Dynamic Placeholders',
        attachFiles: 'Attach files',
        selectAttachments: 'Select attachments',
        removeAttachment: 'Remove {filename}',
        retryAttachment: 'Retry upload for {filename}',
        invalidAttachmentName: '{filename} has an invalid filename.',
        emptyAttachment: '{filename} is empty.',
        attachmentSizeLimit:
          '{filename} was not added. Attachments must fit within the 9 MB email limit.',
        unsupportedAttachment: '{filename} is not a supported file type.',
        attachmentLimit: '{filename} was not added. You can attach up to 10 files.',
        discardTitle: 'Discard email?',
        discardMessage: 'If you leave now, your unsent draft will be lost.',
        keepEditing: 'Keep editing',
        discard: 'Discard',
        variables: {
          firstName: 'First Name',
          lastName: 'Last Name',
          channelHandle: 'Channel Handle',
          campaignName: 'Campaign Name',
          mailingAddress: 'Mailing Address',
          phoneNumber: 'Phone Number',
        },
        links: {
          instagramConnection: 'Instagram Creator Connection',
          tiktokConnection: 'TikTok Creator Connection',
          informationRequest: 'Request for more information',
          tiktokCampaignInvite: 'TikTok Campaign Invite Link',
          affiliate: 'Affiliate Link',
          shopifyGifting: 'Shopify Gifting Link',
        },
      },
    },
    competitive: {
      teaser: {
        heading: 'How do you stack up against the competition?',
        subheading: 'We added industry-leading Instagram competitors to get you started.',
        addCompetitors: 'Add Competitors',
      },
    },
    creators: {
      payments: {
        taxWithholdingBanner: {
          title: 'Tax Withholding Applies',
          message:
            "The creator's tax forms trigger U.S. backup withholding. Your brand keeps the withheld amount to report and remit.",
          learnMore: 'Learn more',
        },
        paymentHistory: {
          taxWithheldTooltip: 'Taxes were withheld from this payment. Click to view details.',
        },
        sendPayment: {
          withholdingHint: {
            withholding: 'Tax Withholding:',
            percentage: '({percentage})',
            creatorReceives: 'Creator Receives:',
            beforeFees: '(before fees)',
            fxRate: "{'|'} FX Rate: {rate}",
          },
        },
      },
    },
    instagram: {
      filters: {
        crossposted: 'Cross posted',
      },
      hashtagMonitor: {
        noUserAccessTitle: "You Don't Have Access to Hashtag Monitoring",
      },
    },
    auth: {
      businessEmail: 'Business Email',
      password: 'Password',
      mustStartWithLetter: 'Must start with a letter',
      logInWithSso: 'Log In With SSO',
      login: {
        forgotPassword: 'Forgot Password',
      },
      accountSelector: {
        pageId: 'Page ID: {id}',
      },
      flightCheckAuthCard: {
        allowed: 'Allowed:',
        notAllowed: 'Not allowed:',
      },
      referralBanner: {
        title: 'Refer Dash and earn $250.',
      },
      accountSetup: {
        welcome: 'Welcome to {appName}',
        intro:
          "We're excited for you to begin collaborating with your team! As a first step, please confirm the following basic information.",
        firstName: 'First Name',
        firstNamePlaceholder: 'Enter first name',
        lastName: 'Last Name',
        lastNamePlaceholder: 'Enter last name',
        jobTitle: 'Job Title',
        jobTitlePlaceholder: 'Enter job title',
        timezone: 'Timezone',
        timezonePlaceholder: 'Search for time zone',
      },
      connect: {
        thankYou: 'Thank You!',
        bluesky: {
          title: 'Connect Your Bluesky Account',
          success: 'Your Bluesky account has been connected to Dash Social.',
        },
        facebook: {
          title: 'Connect Your Instagram Account for',
          success: 'Your Facebook account has been connected to Dash Social.',
        },
        facebookAds: {
          title: 'Connect Your Facebook Ads Accounts for',
          success: 'Your Facebook Ads accounts have been connected to Dash Social.',
        },
        pinterest: {
          title: 'Connect Your Pinterest Account for',
          success:
            'Your account has been connected to Dash Social. We are now analyzing your Pinterest data.',
          featuredArticle: 'Featured article:',
          articleTitleLine1: 'Growing Your Organic',
          articleTitleLine2: 'Pinterest Strategy',
          blogCaption: 'Learn more about visual marketing on {blog}!',
          blog: 'our blog',
        },
        snapchat: {
          title: 'Connect Your Snapchat Account',
          success: 'Your Snapchat account has been connected to Dash Social.',
        },
        threads: {
          title: 'Connect Your Threads Profile',
          success: 'Your Threads profile has been connected to Dash Social.',
        },
        tiktok: {
          title: 'Connect Your TikTok Account for',
          success: 'Your TikTok account has been connected to Dash Social.',
        },
        tiktokAds: {
          title: 'Connect Your TikTok Ads Account for',
          success: 'Your TikTok Ads account has been connected to Dash Social.',
        },
        twitter: {
          title: 'Connect Your X Account for',
          success: 'Your X account has been connected to Dash Social.',
        },
        youtube: {
          title: 'Connect Your YouTube Channel',
          success: 'Your YouTube channel has been connected to Dash Social.',
        },
      },
      creatorConnect: {
        tryAgain: 'Try Again',
        acceptAndConnect: 'Accept & Connect',
        switchIcon: 'switch-horizontal icon',
      },
      forgotPassword: {
        info: 'Please enter your email address to reset the password.',
        reset: 'Reset Password',
      },
      oauthConsent: {
        expiredTitle: 'This Link Has Expired',
        expiredMessage: 'Return to the app you started from and try connecting Dash Social again.',
        logo: 'Dash Social',
        title: 'Connect with Dash Social MCP',
        grant: 'Grant {client} access to Dash Social',
        connectingAs: 'Connecting as:',
        scopes: 'Dash Social MCP will be able to:',
        agreement: 'By continuing, you confirm that you agree to Dash Social’s {privacyPolicy}.',
        privacyPolicy: 'Privacy Policy',
        questions: 'Have questions? {learnMore}',
      },
      onboarding: {
        stepCounter: 'Step {step} of {total}',
        welcome: 'welcome to {appName}',
        getToKnowYou: "Let's get to know you.",
        connectChannels: 'Connect Your Channels',
        connectChannelsHelp:
          'To set up your {appName} account, you will need to connect at least one social channel.',
        notReadyToConnect: 'Not ready to connect?',
        inviteHelp:
          'Additional users are always free with {appName}. Who do you want to collaborate with?',
        skip: 'I’ll do this later',
      },
      otp: {
        scanTitle: 'Scan the QR code',
        scanMessage:
          'MFA (multi-factor authentication) is required to complete your signup. Scan the QR code with your authenticator app.',
        secureTitle: 'Secure Your Account',
        codeLabel: 'Verification Code (6-digit)',
      },
      passwordReset: {
        title: 'Set Your Password',
      },
      setPassword: {
        trustedBy: 'TRUSTED BY TEAMS AT:',
        completeSignup: 'Complete Signup',
        signUpWithGoogle: 'Sign Up With Google',
      },
      ssoLogin: {
        info: 'Please enter the email address you use for your organization.',
      },
      ssoLoginError: {
        imageTitle: 'Page not found',
        title: "Looks like there's a problem with your account",
        contact: '{reason} Please contact us for help!',
        home: 'Take me home',
      },
    },
    landingPage: {
      title: 'Social Entertainment Overview',
      reportingPeriod: 'Reporting Period',
      reportingPeriodTooltip:
        'Activity on posts published or mentions received within the reporting period',
    },
    library: {
      mediaUpload: {
        fileTooLarge:
          'Your media exceeds the 1GB upload limit. Try a smaller file or compress it before uploading.',
        uploadFailed: 'An error occurred while trying to upload your media. Please try again.',
        processingFailed: 'An error occurred while processing your media. Please try again.',
      },
    },
    mediaInsightsDrawer: {
      linkDetails: {
        title: 'Link details',
        swipeUp: 'Swipe up link',
        trackable: 'Trackable link',
        loading: 'Loading trackable link',
      },
      loadingTitle: 'Loading insights title',
      previousMedia: 'Previous media',
      nextMedia: 'Next media',
      loadingLabel: 'Loading media',
      loadingMessage: 'Loading media…',
      metricView: 'Metric view',
      adAccount: 'Ad account: {name}',
      adAccountId: 'Ad Account ID: {id}',
      mediaDetails: 'Media details',
      postInformation: {
        instagramUser: 'Instagram User',
      },
      twitterPreview: {
        loading: 'Loading preview…',
      },
      comments: {
        title: 'Comments',
        list: 'Comments on this post',
        loading: 'Loading comments…',
        loadingMore: 'Loading more comments…',
        loadError: 'Unable to load comments.',
        pageError: 'Unable to load more comments.',
        retry: 'Retry',
        loadMore: 'Load more comments',
        noneVisible: 'No visible comments loaded.',
        noneAvailable: 'No comments available.',
        unknownAuthor: 'Unknown user',
        attachmentOnly: 'Attachment',
        noText: 'No text',
        summarize: 'Summarize',
        summarizeMinimum: 'You need a minimum of {count} eligible comments to use AI Summaries.',
        summarizeCountUnavailable:
          'Unable to check whether this post has enough comments to summarize.',
        sentiment: {
          label: 'Sentiment',
          category: {
            positive: 'Positive',
            neutral: 'Neutral',
            negative: 'Negative',
          },
          dominant: {
            positive: '{share} positive',
            neutral: '{share} neutral',
            negative: '{share} negative',
          },
          share: {
            positive: 'Positive: {share}',
            neutral: 'Neutral: {share}',
            negative: 'Negative: {share}',
          },
        },
      },
    },
    scheduler: {
      postEditor: {
        postUnavailable: 'Unable to load post.',
      },
    },
    primaryNavigation: {
      analytics: 'Analytics',
      campaigns: 'Campaigns',
      community: 'Community',
      competitors: 'Competitors',
      constellationPro: 'Constellation Pro',
      creators: 'Creators',
      dashboards: 'Dashboards',
      homeLink: 'Go to the Dash Social home page',
      library: 'Library',
      listening: 'Listening',
      scheduler: 'Scheduler',
      visionHome: 'Vision Home',
    },
    constellationPro: {
      projectCard: {
        mediaCount: '{count} Media',
      },
    },
    ecommerce: {
      linkCard: {
        addLinks: 'Add Links',
        clicks: '{count} Click | {count} Clicks',
        link: 'Link {index}',
        links: '{count} Link | {count} Links',
        totalClicks: 'Clicks',
      },
    },
    visionAi: {
      chatMedia: {
        addFromLibrary: 'Add media from library',
        uploadFromComputer: 'Upload media from computer',
        removeMedia: 'Remove {name}',
      },
    },
    visionHub: {
      creators: {
        title: 'Creators',
        prompts: {
          discover: 'Discover Creators that fit my brand',
          rank: 'Rank my top performing creators',
          topUgc: 'See Top Performing UGC',
          similar: 'Find Similar Creators',
          recap: 'Recap recent UGC Performance',
        },
        tasks: {
          weekly: 'Weekly',
          performance: {
            name: 'Creator Performance Recap',
            description:
              'Get a weekly breakdown of creator performance across active campaigns, with your top-performing creators ranked and key trends highlighted.',
            instructions:
              'Generate a weekly creator performance report covering each active campaign. For each campaign, summarize overall performance, highlight key trends, and rank the top-performing creators saved in Your Creators based on relevant performance metrics. Include creator-level metrics, standout content, engagement trends, and notable changes week over week. Highlight creators driving the strongest results and surface opportunities to optimize campaign performance.',
          },
          ugc: {
            name: 'UGC Performance Recap',
            description:
              'Track your top-performing UGC, uncover performance trends from the last 30 days, and stay ahead of expired content rights.',
            instructions:
              'Generate a weekly UGC performance report analyzing UGC from the last 30 days. Highlight top-performing content and break down key metrics and performance indicators. Identify emerging trends in content performance, formats, and themes, and call out standout UGC worth repurposing.',
          },
        },
      },
      actionsNeeded: {
        title: 'Actions Needed',
        seeAll: 'See All',
        loadError: 'Could not load Actions Needed.',
        retry: 'Retry',
        noMatches: 'No actions match your filters.',
        pagination: 'Actions Needed pages',
        taskFilter: 'Task',
        statusFilter: 'Status',
        columns: {
          task: 'Task',
          brand: 'Brand',
          lastRun: 'Last Run',
          action: 'Action',
        },
        unread: 'Unread',
        review: 'Review',
        reviewLabel: 'Review {task}',
        reviewError: 'Could not open {task}. Please try again.',
      },
      taskActivity: {
        loadError: 'Could not load activity.',
        retry: 'Retry',
        totals: 'Activity totals',
        emptyTitle: 'No Runs Yet',
        emptyMessage:
          'Click the “Run Task” button at the top to start your task and check that the instructions are working correctly',
        columns: {
          runs: 'Runs',
          action: 'Action',
          date: 'Date',
          status: 'Status',
        },
        unread: 'Unread',
        pagination: 'Run pages',
      },
      taskRun: {
        placeholder: 'How can I help you today?',
        visionHub: 'Vision Hub',
        newChat: 'New chat',
        moreOptions: 'More options',
        approve: 'Approve',
        reject: 'Reject',
        decisionError: 'Could not save your decision. Try again.',
        loading: 'Loading run…',
        queued: 'Waiting to start…',
        working: 'Working on it…',
        loadError: 'Could not load this run.',
        retry: 'Retry',
      },
      lastRunStatus: {
        column: 'Last Run Status',
        labels: {
          queued: 'Queued',
          running: 'Running',
          succeeded: 'Successful',
          failed: 'Failed',
          needsApproval: 'Needs Your Approval',
          succeededWithWarnings: 'Succeeded with Warnings',
          archived: 'Archived',
        },
        tooltips: {
          failed: 'A task failed and needs review',
          needsApproval: 'A task needs approval',
          succeededWithWarnings: 'A task completed with warnings',
        },
      },
      taskSharing: {
        title: 'Share with Team',
        share: 'Share',
        searchUsers: 'Search Users',
        searchBrands: 'Search Brands',
        users: 'Users',
        brands: 'Brands',
        user: 'User',
        email: 'Email',
        access: 'Access',
        changeAccess: 'Change Access',
        userAccess: 'User access',
        clearSelection: 'Clear Selection',
        selectAllUsers: 'Select all users',
        brand: 'Brand',
        add: 'Add',
        viewer: 'Viewer',
        editor: 'Editor',
        removeAccess: 'Remove Access',
        userRoleUpdated: "{name}'s role has been updated on {taskName}.",
        userAccessAdded: '{name} has been added to {taskName}.',
        userAccessRemoved: "{name}'s access has been removed from {taskName}.",
        bulkUserAccessUpdated: 'Access updated for {count} users on {taskName}',
        bulkUserAccessRemoved: 'Access removed for {count} users on {taskName}.',
        selectedCount: 'No items selected | 1 item selected | {count} items selected',
        noUsers: 'No users found',
        noBrands: 'No brands configured',
        noBrandsFound: 'No brands found',
        loadMore: 'Load more',
        retry: 'Retry',
        loading: 'Loading sharing…',
        loadFailed: 'Could not load sharing.',
        usersFailed: 'Could not load eligible users.',
        saveFailed: 'Could not save access. Review selections and retry.',
        accessUnavailable: 'You no longer have access to manage sharing for this task.',
        unknownUser: 'User #{id}',
      },
      scheduledTasks: {
        loadMore: 'Load more tasks',
        resolveHighlightedErrors: 'Resolve the highlighted errors before saving.',
        brandChange: {
          header: 'Update Brands?',
          message:
            'Updating the brands for this scheduled task will reset its view/edit permissions and activity log. Are you sure you want to continue?',
          cancel: 'Cancel',
          confirm: 'Save Task',
          partialFailure:
            'A new task was created, but the original could not be archived. Review the task list before trying again.',
          archiveCleanupFailed:
            'Task updated, but the previous schedule could not be removed. Contact support.',
        },
        scheduleRetry: 'Task was saved, but scheduling failed. Select Save to retry.',
        scheduleActionRetry: 'Task change saved, but scheduling failed. Retry the same action.',
        archiveRetry: 'Task archived, but schedule cleanup failed. Retry Delete Task.',
        retrySavedBrands: 'Restore the saved Brands before retrying this task.',
        selectTime: 'Select Time',
        monthlyPicker: {
          startsOn: 'Starts on',
        },
        scheduleSelect: {
          placeholder: 'Choose how often you want the task to run',
        },
        settingsForm: {
          nameLabel: 'Task Name*',
          namePlaceholder: 'Enter task name',
          descriptionLabel: 'Description',
          descriptionPlaceholder: 'Describe what the task does for you',
          brandsLabel: 'Brands*',
          brandsPlaceholder: 'Select the brands you want the task to run for',
          brandsSearchPlaceholder: 'Search Brands',
          brandsHeader: 'Your Brands',
          scheduleLabel: 'Scheduled*',
          instructionsLabel: 'Task Instructions*',
          instructionsPlaceholder: 'Write custom instructions',
          approvalLockedLabel: 'Approval requirement cannot be changed',
          approvalLabel: 'Approval Required',
          approvalTooltip:
            'If approval is required, the agent will always check with you before running.',
        },
      },
    },
    settings: {
      tagCategories: {
        community: 'Community',
        title: 'Tag Categories',
        sortCategory: 'Tag Category',
        edit: 'Edit Tag Category',
        membership: 'Tags in {name} ({brand})',
        actions: 'Actions for {name} ({brand})',
        searchLabel: 'Search tag categories',
        searchPlaceholder: 'Search category names',
        searchBrands: 'Search brands',
        searchTags: 'Search tags',
        filterBrands: 'Filter by brands',
        categoriesError: 'Tag categories could not be loaded.',
        tagsError: 'Tags could not be loaded.',
        emptyTitle: 'Measure What Matters',
        emptyMessage:
          'Group related Tags into Categories to understand conversation themes across your brands, track trends, and uncover which themes drive engagement.',
        suggestCategories: 'Suggest Categories',
        noResultsTitle: 'No Results Found',
        noResultsMessage: 'Try different keywords, checking for typos, or adjusting your filters',
        loading: 'Loading tag categories',
        pagination: 'Tag categories pagination',
        deleteTitle: 'Delete Selected Categories?',
        deleteMessage:
          'Deleting categories won’t delete their associated tags. Check the box below if you also want to delete those tags.',
        deleteTags: 'Also delete associated tags',
      },
      community: {
        title: 'Community',
        upsellTitle: 'Unlock Sentiment and Spam with Advance',
        sentimentAndSpam: {
          edit: 'Edit sentiment and spam',
          resetToDefault: 'Reset to default',
          definitionFor: {
            POSITIVE: 'Positive definition for {brand}',
            NEGATIVE: 'Negative definition for {brand}',
            SPAM: 'Spam definition for {brand}',
          },
          resetDefinitionFor: {
            POSITIVE: 'Reset Positive to default for {brand}',
            NEGATIVE: 'Reset Negative to default for {brand}',
            SPAM: 'Reset Spam to default for {brand}',
          },
        },
      },
      communityTags: {
        noResultsTitle: 'No Results Found',
        noResultsMessage: 'Try different keywords, checking for typos, or adjusting your filters',
        rowActions: {
          label: 'Actions for {name} ({brand})',
          edit: 'Edit Tag',
          delete: 'Delete Tag',
        },
        deletion: {
          confirmTitle: 'Delete {name}?',
          confirmMessage:
            "Deleting the tags will remove the tags from any message they're currently applied to",
          blockedByAgentTasks:
            'This tag is used in an Agent Task. Remove it from the task before deleting it. | This tag is used in {count} Agent Tasks. Remove it from these tasks before deleting.',
          success: 'Your tag has been deleted',
        },
        emptyState: {
          title: 'Never Miss an Important Message',
          message:
            'Organize conversations by intent and priority to surface important messages, streamline triage, and ensure your team responds to what matters most.',
          suggestTags: 'Suggest Tags',
        },
        categoryPicker: {
          search: 'Search',
          changeLabel: 'Change category for {name} ({brand})',
          addLabel: 'Add category for {name} ({brand})',
          addNew: 'Add New',
        },
        searchLabel: 'Search tags',
        searchPlaceholder: 'Search tag names',
        filters: {
          categories: 'Tag Categories',
          searchBrands: 'Search brands',
          searchCategories: 'Search tag categories',
          uncategorized: 'Uncategorized',
          on: 'On',
          off: 'Off',
        },
        columns: {
          tags: 'Tags',
          category: 'Tag Category',
          brands: 'Brands',
          description: 'Tag Description',
          autoTagging: 'Auto-Tagging',
        },
        tooltips: {
          category:
            'Categories group related Tags into high-level themes, making it easier to measure and understand what your community is talking about.',
          description:
            'Descriptions define what types of messages should receive a Tag. Vision uses the description to identify and automatically tag matching messages.',
          autoTagging:
            'Auto-tagging automatically applies Tags to new messages based on their definitions',
          usedInTask:
            'Auto-tagging can’t be disabled because this tag is being used in an Agent Task. Remove the tag from the task or delete the task first.',
          missingDescription:
            'Auto-tagging requires a tag description to identify matching messages.',
        },
        autoTaggingLabel: 'Auto-tagging for {name} ({brand})',
        autoTaggingUpdateError: 'Auto-tagging could not be updated. Please try again.',
        taskLink: 'See in Agent Task | See in {count} Agent Tasks',
        taskLinkLabel: 'See in Agent Task: {name}',
        loadError: 'Tags could not be loaded.',
        categoriesLoadError: 'Tag categories could not be loaded.',
        tasksLoadError: 'Agent Tasks could not be loaded.',
        noTags: 'No tags found.',
        visionTitle: 'Try Vision',
        visionMessage:
          'Prioritize every message that hits your inbox. Let Vision evaluate your current Tags and suggest Categories and Tags that reflect what your community is saying.',
        suggestCategories: 'Suggest Tag Categories',
        autoTaggingExplanation:
          'Tags will be automatically applied to messages in Community when auto-tagging is enabled. Vision will match messages based on the description provided. Tags can also be applied by an existing Agent Task.',
        loading: 'Loading tags',
        paginationLabel: 'Tags pagination',
      },
      contentTagPicker: {
        upsell: 'Upgrade to the Enterprise Plan to use exclusive content tagging',
        searchPlaceholder: 'Search tags',
        moreCount: '+{count} more',
      },
      authorizedApps: {
        title: 'Authorized Apps',
        heading: 'Keep track of your Dash Social account data',
        intro:
          "You're sharing data with these apps. These apps are created by companies or developers that aren't Dash Social. Only share your data with apps that you trust.",
        app: 'App',
        scope: 'Scope',
        authorized: 'Authorized',
        expires: 'Expires',
        revokeAccess: 'Revoke Access',
        emptyTitle: 'No Authorized Apps Yet',
      },
      billing: {
        socialPlan: 'Social Plan',
        compareSocialPlans: 'Compare All Social Plans',
        creatorPlan: 'Creator Management Plan',
        compareCreatorPlans: 'Compare All Creator Management Plans',
        addOns: 'Advanced Plan Add-Ons',
      },
      brandManagement: {
        title: 'Brands',
        intro:
          'Add tags to your brands to create custom groups and categories. Brand tags can be used to quickly create reports in Dashboards.',
        learnMore: 'Learn more about Brand Tags',
      },
      communityAgent: {
        title: 'Community Management',
        searchPlaceholder: 'Search Community Tasks',
        createTask: 'Create Task',
      },
      competitorManagement: {
        title: 'Competitors',
        add: 'Add Competitor',
      },
      contentAutomation: {
        automationRules: 'Automation Rules',
        create: 'Create Automation',
        learnMore: 'Learn more',
        learnMoreAbout: 'Learn more about content automation',
        promptTitle: 'What would you like to set up today?',
        promptPlaceholder:
          'Ask to keep your content organized by applying tags or add published posts to Campaigns automatically when conditions are met',
        addCondition: 'Add Condition',
        addTo: 'add to {item}',
        addToCampaign: 'add to Campaign',
        allMediaTypesTooltip: 'Applies to all media types for the selected channels',
        and: 'and',
        applyToExisting: 'Apply to Existing Posts',
        brands: 'Brands',
        contentPreview: 'Content Preview',
        contentTagUpgrade: 'Upgrade to the Enterprise Plan to use exclusive content tagging',
        enterpriseOnlyTagged: 'Only brands on Enterprise Plans will be tagged',
        filtersAndActionsFor: 'Filters and Actions for',
        generatingPreview: 'We’re generating your post preview.',
        ifLabel: 'If',
        is: 'is',
        keywordPlaceholder: 'Enter Keyword',
        moreCount: '+{count} more',
        nameYourAutomation: 'Name Your Automation',
        noBrands: 'No Brands',
        noBrandsAvailable: 'No brands available',
        noCampaignsFound: 'No campaigns found',
        noCampaignsReturned: 'No Campaigns found',
        noResults: 'No results.',
        or: 'or',
        owned: 'Owned',
        ownedLabel: 'Owned:',
        rule: 'Rule',
        ruleNamePlaceholder: 'Enter rule name',
        saveAndApply: 'Save & Apply to Existing',
        searchCampaigns: 'Search for Campaigns',
        selectBoard: 'Select board',
        selectBrandsOrTags: 'Select Brands or Brand Tags',
        selectCampaign: 'Select campaign',
        selectCampaigns: 'Select Campaigns',
        selectChannels: 'Select Channels',
        selectContentTags: 'Select Content Tags',
        selectFiltersAndActions: 'Select Content Filters and Actions',
        selectGallery: 'Select gallery',
        selectItems: 'Select {items}',
        stepCounter: 'Step {step} of {total}',
        tag: 'tag',
        thenLabel: 'Then',
        ugc: 'UGC',
        ugcLabel: 'UGC:',
        unavailableBrands: 'Unavailable Brands',
        videoDuration: 'Video Duration',
        visionAi: {
          messagePrompt: 'Message Prompt',
          namePlaceholder: 'Enter name',
          promptHelp:
            'Customize one of our suggested prompts or create your own by describing it in natural language.',
          promptPlaceholder: 'Describe the posts you want to match',
        },
      },
      contentTags: {
        title: 'Content Tags',
        create: 'Create Content Tag',
        intro:
          'Add tags to your posts to create custom categories. Content tags can be used to quickly create custom reports in Dashboards.',
        learnMoreAbout: 'Learn more about Content Tags',
      },
      creatorEmailConnection: {
        title: 'Creator Email Connection',
        provider: 'Provider',
        connectedEmail: 'Connected Email',
        action: 'Action',
        connected: 'Connected',
        connect: 'Connect',
      },
      creatorPayorProfile: {
        title: 'Creator Payor Profile',
        setUp: 'Set Up Payor Profile',
        help: 'Paying Creators help',
        accountApproved: 'Account Approved',
        somethingWentWrong: 'Something Went Wrong',
        paymentMethods: 'Payment Methods',
        method: 'Method',
        status: 'Status',
        fundBalance: 'Fund Balance',
        url: 'URL',
        amount: 'Amount',
        email: 'Email',
      },
      creatorProfileFields: {
        title: 'Creator Profile Fields',
        intro:
          'Tailor creator profiles with custom fields to capture key details, like favorite products or clothing sizes, in Your Creators.',
        learnMoreAbout: '{learnMore} about customizing Your Custom Fields.',
        learnMore: 'Learn more',
        fieldNameEdited: 'Field Name Edited',
        fieldName: 'Field Name',
        fieldNamePlaceholder: 'e.g. T-shirt Size',
        duplicateName: 'Field name already exists',
        visibility: 'Visibility',
        action: 'Action',
        addField: 'Add Field',
      },
      customMetrics: {
        tryAgain: 'Try Again',
        title: 'Custom Metrics',
        fetchError: 'Failed to fetch details for custom metrics',
      },
      developers: {
        brandId: '(Brand ID: {id})',
        apiKeyManagement: 'API Key Management',
        apiDocumentation: 'API Documentation',
        generateApiKey: 'Generate New API Key',
      },
      editBrandPayor: {
        title: 'Payor Profile',
        monthlyPayments: 'Expected Monthly Payments (in USD)',
        monthlyPaymentsPlaceholder: "e.g., '10,000'",
        paymentCount: 'Estimated Number of Payments Per Month',
        paymentCountPlaceholder: "e.g., '100'",
        international: 'Payments Sent Internationally (%)',
        internationalPlaceholder: "e.g., '20'",
        billingEmail: 'Billing Contact Email',
      },
      emailNotifications: {
        sendReport: 'Send Report',
        userPreferences: 'User Email Preferences',
        userPreferencesHelp: 'The below settings are not specific to a brand',
      },
      integrations: {
        authError: 'Unable to authenticate to integration service.',
        emptyTitle: 'No Integrations Found',
      },
      payor: {
        payeeAccessLink: 'Payee Access Link',
        description: 'Description',
        paymentEstimates: 'Payment Estimates',
        businessDetails: 'Business Details',
      },
      performanceAlerts: {
        title: 'Performance Alerts',
        upsellTitle: 'Stay Ahead with Performance Alerts',
        promptTitle: 'What would you like to set up today?',
        promptPlaceholder:
          'Ask to set alerts for engagement spikes, viral posts, or performance drops by defining what you want to monitor',
        emptyTitle: 'No Performance Alerts Found',
      },
      platformConnections: {
        filters: {
          googleAnalyticsTooltip:
            'Connect your Google Analytics account to see website traffic, transactions, and revenue in Campaigns',
        },
        bulkDisconnect: {
          warning:
            "Insights and content will no longer be imported into Dash Social and you won't be able to publish any posts.",
        },
        title: 'Platform Connections',
        searchBrands: 'Search brands',
        searchPlaceholder: 'Search for brand',
        controls: 'Platform connection controls',
        disconnect: 'Disconnect',
        noResults: 'No Results Found',
        loadError: 'Platform connections could not be loaded.',
        tryAgain: 'Try again',
      },
      privacyNotice: {
        title: 'Privacy Notice',
        message:
          "Dash Social's Web Tracker records page URLs and page views to measure content performance and does not store information identifying individual website visitors.",
      },
      profile: {
        title: 'Profile',
        changePassword: 'Change Password',
        uploadPhoto: 'Upload Photo',
        organization: 'Organization',
        email: 'Email',
        firstName: 'First Name',
        firstNamePlaceholder: 'Enter your first name',
        lastName: 'Last Name',
        lastNamePlaceholder: 'Enter your last name',
        jobTitle: 'Job Title',
        jobTitlePlaceholder: 'Enter your job title',
        timezone: 'Preferred Timezone',
        timezonePlaceholder: 'Search for time zone',
      },
      publishingAutopilot: {
        title: 'Publishing Autopilot',
        createRule: 'Create Publishing Rule',
        applyUtms: 'Apply UTMs',
        articleAgeLimit: 'Article Age Limit',
        autoPublish: 'Auto Publish',
        avoidConflicts: 'Avoid Conflicts',
        bestTimeToPost: 'Use Best Time to Post',
        breakingNewsAlerts: 'Breaking News Alerts',
        channelPlaceholder: 'Enter Channel',
        contentMix: 'Content Mix',
        emptyTitle: 'Create a Publishing Rule to optimize your post schedules',
        evergreenPct: 'Evergreen {pct}%',
        excludedKeywords: 'Excluded Keywords',
        excludedKeywordsPlaceholder: 'Enter keywords to exclude',
        feedUrlPlaceholder: 'https://example.com/feed.xml',
        filters: {
          channels: 'Channels',
          dataSource: 'Data Source',
          priority: 'Priority',
          ruleType: 'Rule Type',
        },
        includedKeywords: 'Included Keywords',
        includedKeywordsPlaceholder: 'Enter keywords to include',
        keywordsFiltering: 'Keywords Filtering',
        limitPlaceholder: 'Enter Limit',
        manageConnections: 'Manage Connections',
        minutesPlaceholder: 'Enter Minutes',
        newContentPct: 'New Content {pct}%',
        noRuleFound: 'No rule found',
        organizationAndReporting: 'Organization and Reporting',
        postCaption: 'Post Caption',
        postCaptionPlaceholder: 'Choose Post Caption',
        postType: 'Post Type',
        postTypePlaceholder: 'Select Post Type',
        priorityPlaceholder: 'Select priority',
        publishingLimit: 'Publishing Limit',
        publishingRules: 'Publishing Rules',
        publishingSettings: 'Publishing Settings',
        webTracker: {
          companyTagPlaceholder: 'Enter company tag',
          companyTags: 'Company Tags',
          copy: 'Copy Tracker Script',
          delete: 'Delete Script',
          domainUrl: 'Domain URL',
          domainUrlPlaceholder: 'https://',
          generate: 'Generate Tracker Script',
          howToInstall: 'How to install to your website.',
          installHelp:
            '{howToInstall} View our {privacyNotice} for details on what the script tracks.',
          trackerScript: 'Tracker Script',
        },
      },
      scheduledEmails: {
        dashboards: 'Dashboards',
      },
      shareableLinks: {
        title: 'Shareable Links',
        createdByYou: 'Links Created by You',
        availability: 'Availability',
        dashboardName: 'Dashboard Name',
        createdBy: 'Created By',
      },
      teamAndPermissions: {
        addUser: 'Add User',
      },
      utm: {
        howTo: 'How to Set Up and Track Performance with UTMs',
      },
      visionAgentsMoved: {
        title: 'Vision Agents',
        heading: 'We’ve Moved Our Agents to Vision',
      },
      communityCreateMenu: {
        create: 'Create',
        tagCategory: 'Tag category',
      },
      tagCategoryDrawer: {
        createTitle: 'Create Tag Category',
        editTitle: 'Edit Tag Category',
        brand: 'Brand',
        brandPlaceholder: 'Select brand',
        name: 'Category Name',
        namePlaceholder: 'Enter tag category name',
        color: 'Color',
        colorTooltip: 'Tags in this category display this color.',
        colorPlaceholder: 'Select color',
        tags: 'Tags in Category',
        tagsTooltip:
          'Each tag can belong to one category. Adding a tag moves it out of its current category.',
        tagsPlaceholder: 'Select tags to add',
        selectBrandFirst: 'Select a brand first to add tags',
        noSavedTags: 'Save a tag for this brand before creating a tag category.',
        duplicateName: 'Tag category already exists',
        createError: 'Can’t create tag category, please try again',
        updateError: 'Can’t update tag category, please try again',
        created: 'Tag category created',
        updated: 'Tag category updated',
        limitReached: 'Each brand can have up to {count} tag categories.',
      },
    },
  },
  metrics: {},
  errors: {},
  labels: {
    tagCategoryColor: {
      LUCKY_CHARM: 'Lucky charm',
      POLAR_DRIFT: 'Polar drift',
      PLUM_PUNCH: 'Plum punch',
      ROSE_QUARTZ: 'Rose quartz',
      GOLDEN_HOUR: 'Golden hour',
      CLAY_SUNSET: 'Clay sunset',
      MINT_BREEZE: 'Mint breeze',
      LAVENDER_HAZE: 'Lavender haze',
      OCEAN_GLOW: 'Ocean glow',
      COCOA_TREE: 'Cocoa tree',
      COBALT_SURGE: 'Cobalt surge',
      BLUEBERRY_BLAST: 'Blueberry blast',
      MERMAID_SPLASH: 'Mermaid splash',
      KELP_FOREST_FROLIC: 'Kelp forest frolic',
      UNICORN_WHISKERS: 'Unicorn whiskers',
      DENIM_DAYDREAM: 'Denim daydream',
      BUBBLEGUM_BONANZA: 'Bubblegum bonanza',
      SKY_HIGH_SURPRISE: 'Sky high surprise',
      VIOLET_VORTEX: 'Violet vortex',
      CHERRY_BLAZE: 'Cherry blaze',
      MAJESTIC_MUFFIN: 'Majestic muffin',
      LILAC_MIST: 'Lilac mist',
    },
  },
} as const;
